<template>
  <ui-overlay-loader :loading="loading">
    <template v-slot:loader-content>
      <br />
      <ui-button @click="reload">Retry?</ui-button>
    </template>
    <div v-if="isAutoCapture" class="img-title">
      {{ $t("identification.auto-capture-image") }}
    </div>
    <figure class="figure">
      <div class="photo">
        <div
          ref="imgWrap"
          :class="[
            'img-wrap',
            { 'img-drag-enabled': isZoomLocked, 'img-dragging': isPointerDown }
          ]"
          @pointermove="handlePointerMove"
          @pointerenter="handlePointerMove"
          @pointerleave="handlePointerLeave"
          @click="handleClick"
          @dblclick="handleDoubleClick"
          @pointerdown="handlePointerDown"
          @pointerup="endDrag"
          @pointercancel="endDrag"
          @lostpointercapture="endDrag"
          @dragstart.prevent
        >
          <div
            :class="{
              'img-zoom': !isZoomLocked
            }"
            :style="imgZoomStyle"
          >
            <div ref="identificationPhoto" class="img" :style="imgStyle"></div>
          </div>
        </div>
        <div v-if="error" class="img-error">
          {{ error }}
          <ui-button @click="reload">Reload</ui-button>
        </div>
      </div>
    </figure>
    <div class="controls">
      <span :title="caption">
        <icon :name="icon" class="photo-icon" />
      </span>
      <slot name="controls" />
      <ui-button class="control" @click="rotation += 90">
        <icon name="rotate-right" class="control-icon" />
      </ui-button>
      <ui-button class="control" @click="openImage" :disabled="!objectUrl">
        <icon name="open-in-new" class="control-icon" />
      </ui-button>
    </div>
  </ui-overlay-loader>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { MEDIA_DOCUMENT_SIDE, MEDIA_FILE_TYPE, ZOOM_LEVELS } from "@/constants";
import { actions, getters } from "@/store";

export default {
  props: {
    fileType: String,
    givenDocumentSideType: String,
    givenDocumentType: String,
    fileExtension: String,
    url: String,
    autoCaptureConfidenceRate: Number
  },
  data() {
    return {
      loading: false,
      error: null,
      rotation: 0,
      scale: ZOOM_LEVELS.NORMAL,
      objectUrl: null,
      imageRequestController: null,
      isZoomLocked: false,
      isPointerDown: false,
      capturedPointerId: null,
      zoomOriginX: null,
      zoomOriginY: null,
      dragStartX: null,
      dragStartY: null,
      draggedDistanceX: 0,
      draggedDistanceY: 0
    };
  },
  computed: {
    ...mapGetters([getters.currentIdentification]),
    icon() {
      switch (this.fileType) {
        case MEDIA_FILE_TYPE.FACE_PHOTO:
        case MEDIA_FILE_TYPE.FACE_ANIMATION:
          return "account";
        case MEDIA_FILE_TYPE.NFC_FACE:
          return "nfc";
        case MEDIA_FILE_TYPE.DOCUMENT_PHOTO:
          if (this.givenDocumentSideType === MEDIA_DOCUMENT_SIDE.FRONT) {
            return "card-account-details";
          } else if (this.givenDocumentSideType === MEDIA_DOCUMENT_SIDE.BACK) {
            return "card-text";
          } else {
            return "help-circle";
          }
        default:
          return "help-circle";
      }
    },
    defaultCaption() {
      let caption = this.fileType;
      if (this.givenDocumentSideType) caption += this.givenDocumentSideType;
      return caption;
    },
    caption() {
      switch (this.fileType) {
        case MEDIA_FILE_TYPE.FACE_PHOTO:
          return this.$t("media_face_photo");
        case MEDIA_FILE_TYPE.FACE_ANIMATION:
          return this.$t("media_face_animation");
        case MEDIA_FILE_TYPE.DOCUMENT_PHOTO:
          if (this.givenDocumentSideType === MEDIA_DOCUMENT_SIDE.FRONT) {
            return this.$t("media_document_front");
          } else if (this.givenDocumentSideType === MEDIA_DOCUMENT_SIDE.BACK) {
            return this.$t("media_document_back");
          } else {
            return this.defaultCaption;
          }
        case MEDIA_FILE_TYPE.CUSTOM_IMAGE:
          return this.$t("media_custom_image_photo");
        default:
          return this.defaultCaption;
      }
    },
    imgStyle() {
      return {
        backgroundImage: this.objectUrl ? `url(${this.objectUrl})` : "",
        transform: `rotate(${this.rotation}deg)`
      };
    },
    imgZoomStyle() {
      return {
        transform: `scale(${this.scale})`,
        transformOrigin: `${this.zoomOriginX}px ${this.zoomOriginY}px`,
        translate: `${this.draggedDistanceX}px ${this.draggedDistanceY}px`
      };
    },
    angle() {
      return this.rotation % 360;
    },
    rotatedImageStyling() {
      switch (this.angle) {
        case 270:
          return {
            transform: "translate(-100%, 0%) rotate(270deg)",
            transformOrigin: "right top"
          };
        case 180:
          return {
            transform: "translate(100%, -100%) rotate(180deg)",
            transformOrigin: "left bottom"
          };
        case 90:
          return {
            transform: "translate(0%, -100%) rotate(90deg)",
            transformOrigin: "left bottom"
          };
        case 0:
        default:
          return { transform: "", transformOrigin: "" };
      }
    },
    isAutoCapture() {
      return typeof this.autoCaptureConfidenceRate === "number";
    }
  },
  watch: {
    url: {
      handler() {
        this.loadDataUrl();
      },
      immediate: true
    }
  },
  mounted() {
    window.addEventListener("pointerup", this.endDrag);
    window.addEventListener("pointercancel", this.endDrag);
    window.addEventListener("mouseup", this.endDrag);
    window.addEventListener("blur", this.endDrag);
    window.addEventListener("keydown", this.handleKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("pointerup", this.endDrag);
    window.removeEventListener("pointercancel", this.endDrag);
    window.removeEventListener("mouseup", this.endDrag);
    window.removeEventListener("blur", this.endDrag);
    window.removeEventListener("keydown", this.handleKeyDown);
    this.endDrag();
    this.imageRequestController?.abort();
    this.imageRequestController = null;
    this.revokeObjectUrl();
  },
  methods: {
    ...mapActions([actions.getIdentification]),
    revokeObjectUrl() {
      if (!this.objectUrl) {
        return;
      }

      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    },
    async loadDataUrl() {
      this.imageRequestController?.abort();
      const controller = new AbortController();
      this.imageRequestController = controller;
      this.revokeObjectUrl();
      try {
        this.loading = true;
        this.error = null;
        const response = await fetch(this.url, {
          method: "GET",
          mode: "cors",
          credentials: "include",
          signal: controller.signal
        });
        const blob = await response.blob();
        // The photo may have changed or been destroyed while the body loaded.
        // A late blob URL would otherwise outlive the component's cleanup.
        if (controller.signal.aborted) {
          return;
        }
        this.objectUrl = URL.createObjectURL(blob);
        if (response.status !== 200) {
          this.error = `${response.status}: ${response.statusText}`;
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        this.$trackException({
          exception: error,
          properties: { source: "component/IdentificationPhoto/loadDataUrl" }
        });
        this.error = error.message;
      } finally {
        if (this.imageRequestController === controller) {
          this.imageRequestController = null;
          this.loading = false;
        }
      }
    },
    reload() {
      this.getIdentification({
        identificationId: this.currentIdentification?.identificationId
      });
    },
    openImage() {
      const image = new Image();
      image.src = this.objectUrl;
      const { transform, transformOrigin } = this.rotatedImageStyling;
      image.style.setProperty("transform", transform);
      image.style.setProperty("transform-origin", transformOrigin);
      const popup = window.open();
      popup.document.write(image.outerHTML);
    },
    handlePointerLeave() {
      // While the pointer is captured the drag deliberately continues outside
      // the image; the capture guarantees a closing pointerup or pointercancel.
      if (this.capturedPointerId !== null) {
        return;
      }

      this.endDrag();
    },
    handlePointerMove(event) {
      // The release can still be missed when the drag leaves the browser or the
      // tab loses focus; a move with no button held down reveals it.
      if (this.isPointerDown && !(event.buttons & 1)) {
        this.endDrag();
      }

      if (!this.isZoomLocked) {
        this.handleHover(event);
        return;
      }

      if (this.isPointerDown) {
        this.handleDrag(event);
      }
    },
    handleHover(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      this.zoomOriginX = Math.round(x);
      this.zoomOriginY = Math.round(y);
    },
    handleDrag(event) {
      const wrapper = event.currentTarget;
      const wrapperRectangle = wrapper.getBoundingClientRect();
      const imageRectangle = this.$refs.identificationPhoto.getBoundingClientRect();

      this.draggedDistanceX += this.calculateMovement({
        currentPosition: event.clientX,
        previousPosition: this.dragStartX,
        firstEdge: imageRectangle.left,
        firstEdgeMax: wrapperRectangle.left,
        secondEdge: imageRectangle.right,
        secondEdgeMin: wrapperRectangle.right
      });

      this.draggedDistanceY += this.calculateMovement({
        currentPosition: event.clientY,
        previousPosition: this.dragStartY,
        firstEdge: imageRectangle.top,
        firstEdgeMax: wrapperRectangle.top,
        secondEdge: imageRectangle.bottom,
        secondEdgeMin: wrapperRectangle.bottom
      });

      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
    },
    calculateMovement({
      currentPosition,
      previousPosition,
      firstEdge,
      firstEdgeMax,
      secondEdge,
      secondEdgeMin
    }) {
      if (!previousPosition) {
        return 0;
      }
      const movement = currentPosition - previousPosition;

      // If new first edge value would be bigger than allowed (positive movement) - don't move
      if (firstEdge >= firstEdgeMax && movement > 0) {
        return Math.round(firstEdgeMax - firstEdge);
      }

      // If new second edge value would be smaller than allowed (negative movement) - don't move
      if (secondEdge <= secondEdgeMin && movement < 0) {
        return Math.round(secondEdgeMin - secondEdge);
      }

      return movement;
    },
    handleClick() {
      this.isZoomLocked = true;
    },
    handleDoubleClick() {
      if (this.scale !== ZOOM_LEVELS.ENHANCED) {
        this.scale = ZOOM_LEVELS.ENHANCED;
        return;
      }

      this.resetZoom();
    },
    resetZoom() {
      this.endDrag();
      this.scale = ZOOM_LEVELS.NORMAL;
      this.isZoomLocked = false;
      this.draggedDistanceX = 0;
      this.draggedDistanceY = 0;
    },
    handleKeyDown(event) {
      // Keyboard still works while the mouse is stuck, so Escape stays a way
      // out of a locked zoom without reloading the page.
      if (event.key !== "Escape" || !this.isZoomLocked) {
        return;
      }

      this.resetZoom();
    },
    handlePointerDown(event) {
      if (!event.isPrimary || event.button !== 0) {
        return;
      }
      // Without this the browser starts its own drag/selection session, which
      // swallows the matching release and leaves the whole page unclickable.
      event.preventDefault();
      this.capturePointer(event);
      this.isPointerDown = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
    },
    capturePointer(event) {
      const wrapper = event.currentTarget;
      if (!wrapper?.setPointerCapture) {
        return;
      }

      try {
        wrapper.setPointerCapture(event.pointerId);
        this.capturedPointerId = event.pointerId;
      } catch (error) {
        // The pointer is already gone; the window listeners end the drag.
      }
    },
    releasePointer() {
      if (this.capturedPointerId === null) {
        return;
      }

      const pointerId = this.capturedPointerId;
      this.capturedPointerId = null;
      const wrapper = this.$refs.imgWrap;
      if (!wrapper?.hasPointerCapture?.(pointerId)) {
        return;
      }

      wrapper.releasePointerCapture(pointerId);
    },
    endDrag() {
      this.releasePointer();
      this.isPointerDown = false;
      this.dragStartX = null;
      this.dragStartY = null;
    }
  }
};
</script>

<style scoped lang="scss">
.figure {
  position: relative;
  margin: 0;
  overflow: hidden;
}
.photo {
  position: relative;
  z-index: 0;
  box-sizing: border-box;
  flex: 0 0 auto;
  overflow: hidden;
}
.photo-icon {
  width: 2rem;
  margin-right: 0.5rem;
  cursor: help;
}
.control.control {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;

  &:not(:first-child) {
    margin-left: 0.5rem;
  }
}
.controls {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
  border: 1px solid var(--button-default-border-color);
  border-top: 0;
  border-radius: 0 0 0.5rem 0.5rem;
}
.control-icon {
  display: inline-block;
  width: 1.4rem;
  fill: var(--button-default-text-color);
}
.img-wrap {
  // The drag is driven by captured pointer events, so the browser must not
  // claim the gesture for a selection, a native drag or a touch scroll.
  touch-action: none;
  cursor: initial;
  user-select: none;
  background-color: var(--container-background);
  border: 1px solid var(--button-default-border-color);
  border-bottom: 0;

  &.img-drag-enabled {
    cursor: grab;
  }

  &.img-dragging {
    cursor: grabbing;
  }
}
.img-title {
  padding: 0.75rem 0;
  font-weight: 700;
  border: 1px solid var(--button-default-border-color);
  border-bottom: 0;
}
.img-zoom {
  pointer-events: none;
  transition-duration: var(--animation-ultra-fast);
  transition-property: transform;
  .img-wrap:not(:hover) & {
    transform: scale(1) !important;
  }
}
.img {
  display: block;
  width: 23rem;
  max-width: 100%;
  padding-bottom: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  transition-duration: var(--animation-fast);
  transition-property: transform;
}
.img-error {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: auto;
}
</style>
